/**
 * bookingCancellation — Centralized cancellation guard for booking views.
 *
 * Single source of truth for "is this booking cancelled?" used by every
 * customer-facing booking surface (BookingStatusCard, CustomerBookingTimeline,
 * EventDetail vendor tracker, PaymentStatusCard).
 *
 * Why a shared util:
 *   Cancellation must take precedence over deposit_paid / final_paid /
 *   agreement state / countdowns / on-track banners — but the historical
 *   payment booleans MUST be preserved for refund tracking and audit. Every
 *   render surface therefore needs the same short-circuit guard at the top
 *   of its decision tree.
 *
 * Returns:
 *   isBookingCancelled(booking) -> boolean
 *   getCancellationSummary(booking) -> {
 *     cancelled: boolean,
 *     cancelledBy: 'customer' | 'vendor' | null,
 *     reason: string | null,
 *     cancelledAt: ISO string | null,
 *     refundStatus: 'refunded' | 'partial_refund' | 'no_refund' | 'refund_failed' | null,
 *     refundAmount: number | null,
 *     headline: string,         // short label, e.g. "Booking cancelled by vendor"
 *     subtext: string,          // descriptive subtext including refund disposition
 *   }
 */

const CANCELLED_STATUSES = ['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'];

export const isBookingCancelled = (booking) => {
  if (!booking) return false;
  return CANCELLED_STATUSES.includes(booking.status);
};

const refundCopy = (refundStatus, refundAmount) => {
  switch (refundStatus) {
    case 'refunded':
      return refundAmount != null
        ? `Full refund of $${Number(refundAmount).toFixed(2)} processed.`
        : 'Full refund processed.';
    case 'partial_refund':
      return refundAmount != null
        ? `Partial refund of $${Number(refundAmount).toFixed(2)} processed.`
        : 'Partial refund processed.';
    case 'no_refund':
      return 'No refund issued per cancellation policy.';
    case 'refund_failed':
      return 'Refund processing encountered an issue — our team has been notified.';
    default:
      return 'See conversation for refund details.';
  }
};

export const getCancellationSummary = (booking) => {
  if (!isBookingCancelled(booking)) {
    return { cancelled: false, cancelledBy: null, reason: null, cancelledAt: null, refundStatus: null, refundAmount: null, headline: '', subtext: '' };
  }

  const cancelledBy =
    booking.cancelled_by === 'vendor' ? 'vendor'
    : booking.cancelled_by === 'customer' ? 'customer'
    : (booking.status === 'cancelled_by_vendor' ? 'vendor'
       : booking.status === 'cancelled_by_customer' ? 'customer'
       : null);

  const headline = cancelledBy === 'vendor'
    ? 'Booking cancelled by provider'
    : cancelledBy === 'customer'
    ? 'Booking cancelled'
    : 'Booking cancelled';

  const refundStatus = booking.refund_status || null;
  const refundAmount = booking.refund_amount != null ? Number(booking.refund_amount) : null;
  const subtext = refundCopy(refundStatus, refundAmount);

  return {
    cancelled: true,
    cancelledBy,
    reason: booking.cancellation_reason || null,
    cancelledAt: booking.cancelled_at || null,
    refundStatus,
    refundAmount,
    headline,
    subtext,
  };
};
